import React, { useCallback, useMemo, useState } from 'react'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import MaldivesDirectSearchFlightCalendarMobile from 'components/MaldivesDirect/MaldivesDirectSearchPage/MaldivesDirectCalendar/MaldivesDirectSearchFlightCalendarMobile'
import { useOfferCharterCalendarData } from 'components/MaldivesDirect/MaldivesDirectSearchPage/useFlightCalendarData'
import { CHARTER_BUNDLE_DURATION_OPTIONS, MALDIVES_CHARTER_END_TARGET_DATE, MALDIVES_CHARTER_START_TARGET_DATE } from 'constants/charterFlight'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { FlightsClassTypes } from 'constants/flight'
import moment from 'moment'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { parseAsBoolean, useQueryState } from 'nuqs'
import useCharterFlightJourneys from 'hooks/CharterFlights/useCharterFlightJourneys'

interface StateProps {
  offer: App.HotelOffer
  duration: number
  rooms: Array<App.Occupants>
  checkInDate?: string
  multipleRoomBookingActive: boolean
  charterBundleRoute: App.CharterBundleRoute
}

interface Props {
  fareClass: FlightsClassTypes
  priceMode: 'total' | 'perPerson'
  onDurationChange: (nights: number) => void
  onClose?: () => void
}

/**
 * Charter Bundle Calendar Modal for Mobile
 * Fetches its own data using useOfferCharterCalendarData hook
 * Follows the pattern of OfferCalendarSidebarDatePicker - dispatches dates internally
 */
function FlashMobileStickyHeaderFiltersCharterCalendarModal(props: Props & StateProps) {
  const {
    offer,
    duration,
    rooms,
    checkInDate,
    multipleRoomBookingActive,
    charterBundleRoute,
    fareClass,
    priceMode,
    onDurationChange,
    onClose,
  } = props

  const modalElement = useModalElementContext()
  const dispatch = useOfferPageDispatch()

  // Read luxPlus state from URL (same as parent component)
  const [luxPlusEnabled, setLuxPlusEnabled] = useQueryState(
    'luxPlus',
    parseAsBoolean.withDefault(true)
  )

  // Get dateRangesByMonth from charter flight journeys API
  // Use fixed date range to fetch all available flights, then extract all dates from the response
  const { dateRangesByMonth: allDateRangesByMonth } = useCharterFlightJourneys({
    charterBundleRoute,
    departDate: MALDIVES_CHARTER_START_TARGET_DATE,
    returnDate: MALDIVES_CHARTER_END_TARGET_DATE,
    occupancies: rooms,
    fareClass,
    disabled: rooms.length === 0,
  })

  // Filter dateRangesByMonth by selected nights
  // Note: We filter by durationDays instead of nights because durationDays represents the total trip duration
  // which matches the user's selection (5 nights = ~7 days, 12 nights = ~14 days)
  const dateRangesByMonth = useMemo(() => {
    // Map selected nights to expected durationDays
    // 5 nights bundle = ~7 days total trip (departure to return)
    // 12 nights bundle = ~14 days total trip (departure to return)
    const expectedDurationDays = duration === 5 ? 7 : 14

    const filtered: Record<string, Array<{ departureDate: string; returnDate: string; checkIn: string; checkOut: string; durationDays: number; nights: number }>> = {}

    Object.entries(allDateRangesByMonth).forEach(([month, ranges]) => {
      // Filter by durationDays which represents the total trip duration
      const filteredRanges = ranges.filter(range => range.durationDays === expectedDurationDays)
      if (filteredRanges.length > 0) {
        filtered[month] = filteredRanges
      }
    })

    return filtered
  }, [allDateRangesByMonth, duration])

  // Manage selected month state internally
  const [selectedMonth, setSelectedMonth] = useState<string>(
    checkInDate ? moment(checkInDate).format('MMM') : moment().format('MMM')
  )

  // Fetch charter calendar data
  const flightCalendarData = useOfferCharterCalendarData({
    offer,
    nights: duration,
    rooms,
    fareClass,
    dateRangesByMonth,
    multipleRoomBookingActive,
    charterBundleRoute,
  })

  const handleDateSelect = useCallback((option: { departDate: string, returnDate: string, checkIn: string, checkOut: string }) => {
    // Like OfferCalendarSidebarDatePicker, dispatch dates internally
    // Use checkIn and checkOut (inner dates) for hotel queries
    dispatch({
      type: OfferPageActions.setDates,
      checkIn: option.checkIn,
      checkOut: option.checkOut,
    })
    dispatch({ type: OfferPageActions.closeHeaderFilter })
    onClose?.()
    modalElement.resolve()
  }, [dispatch, onClose, modalElement])

  const handleClose = useCallback(() => {
    onClose?.()
    modalElement.resolve()
  }, [modalElement, onClose])

  const selectedOptionId = useMemo(() => {
    return checkInDate ? `${checkInDate}-${duration}n` : undefined
  }, [checkInDate, duration])

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title="Select your trip dates"
        onCloseButtonClick={handleClose}
      />
      <ModalBody>
        <ModalContent>
          <MaldivesDirectSearchFlightCalendarMobile
            onDateSelect={handleDateSelect}
            nights={duration}
            occupancies={rooms}
            luxPlusEnabled={luxPlusEnabled}
            onLuxPlusToggle={setLuxPlusEnabled}
            onDurationChange={onDurationChange}
            durationOptions={CHARTER_BUNDLE_DURATION_OPTIONS}
            itemBundleDataByDate={flightCalendarData.itemBundleDataByDate}
            dateRangesByMonth={dateRangesByMonth}
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            fareClass={fareClass}
            priceMode={priceMode}
            selectedOptionId={selectedOptionId}
            mode="offer"
          />
        </ModalContent>
      </ModalBody>
    </ModalBase>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer!,
  duration: state.duration,
  rooms: state.rooms,
  checkInDate: state.checkInDate,
  airport: state.airport,
  multipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
  charterBundleRoute: state.charterBundleRoute!,
}), FlashMobileStickyHeaderFiltersCharterCalendarModal)
